/**
 * The pieces every profile screen is built from.
 *
 * The profile has one rule for saving, and these carry it out. What is tapped
 * — a switch, a choice, a colour, a new order — is kept at once, and says so
 * with a small "Saved" beside the thing touched, which then fades. What is
 * typed is kept with the one Save at the foot of its screen. It used to be
 * five rules on one page: two Save buttons, toasts for some changes, silence
 * for others, and names that saved when the box lost focus.
 *
 * Used by: the profile screens in this folder (SettingRow, SettingList,
 * Segmented, SavedTick, SaveFoot).
 *
 * @module profileKit
 */

import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { Button } from '@/shared/components/ui/button';
import { FailureText } from '@/shared/components/ServerFailure';

/** The word itself. Announced once, politely, to a screen reader. */
export const SavedTick: React.FC<{ show: boolean; className?: string }> = ({ show, className }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <span
      aria-live="polite"
      className={cn(
        'inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary transition-opacity duration-300 motion-reduce:transition-none',
        show ? 'opacity-100' : 'opacity-0',
        className,
      )}
    >
      {show && (
        <>
          {t('profile.saved')}
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
        </>
      )}
    </span>
  );
};

/**
 * One setting: its name and a line under it on the left, the control on the
 * right, and "Saved" between them when it has just been kept. A control too
 * wide to sit beside the name goes under it instead.
 */
export const SettingRow: React.FC<{
  title: string;
  hint?: React.ReactNode;
  saved?: boolean;
  stacked?: boolean;
  htmlFor?: string;
  children: React.ReactNode;
}> = ({ title, hint, saved = false, stacked = false, htmlFor, children }) => (
  <div className={cn('px-4 py-3', stacked ? 'space-y-2' : 'flex items-center gap-3')}>
    <div className={cn('min-w-0', !stacked && 'flex-1')}>
      <div className="flex items-center gap-2">
        {htmlFor
          ? <label htmlFor={htmlFor} className="text-[0.9375rem] font-medium">{title}</label>
          : <span className="text-[0.9375rem] font-medium">{title}</span>}
        {stacked && <SavedTick show={saved} className="ml-auto" />}
      </div>
      {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
    </div>
    {!stacked && <SavedTick show={saved} />}
    {children}
  </div>
);

/** A white box of settings with lines between them. */
export const SettingList: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('divide-y overflow-hidden rounded-lg border bg-card', className)}>{children}</div>
);

/**
 * A choice of two to four, side by side. Dropdowns are kept for long lists,
 * like the states; a choice this short is quicker seen than opened.
 */
export function Segmented<T extends string>({
  value, options, onChange, label, disabled,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (next: T) => void;
  label: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex max-w-full shrink-0 overflow-hidden rounded-md border">
      {options.map((option, index) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => { if (!on) onChange(option.value); }}
            className={cn(
              'min-h-9 whitespace-nowrap px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50',
              index > 0 && 'border-l',
              on ? 'bg-primary/10 font-semibold text-primary' : 'bg-card text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The foot of a screen whose fields are typed: what state they are in, and
 * the one Save, shown once something changes. It used to sit greyed beside
 * "Everything here is saved", which said two things at once.
 *
 * A save's failure is drawn with FailureText, so the standard sentence for a
 * failure on our side (D31) shows the support address as a link.
 */
export const SaveFoot: React.FC<{
  dirty: boolean;
  saving: boolean;
  error?: string | null;
  justSaved?: boolean;
  onSave: () => void;
}> = ({ dirty, saving, error, justSaved, onSave }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <>
      <p
        aria-live="polite"
        className={cn('min-w-0 flex-1 text-sm', error ? 'text-destructive' : 'text-muted-foreground')}
      >
        {error ? <FailureText text={error} /> : (dirty ? t('profile.unsaved') : justSaved ? t('profile.savedAll') : t('profile.allSaved'))}
      </p>
      {(dirty || saving) && (
      <Button type="button" size="lg" className="h-11 shrink-0 px-5" onClick={onSave} disabled={!dirty} loading={saving}>
        {t('sitewide.save')}
      </Button>
      )}
    </>
  );
};
